import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import ProgressBar from '../../components/ProgressBar';
import { useAuth } from '../../context/AuthContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import {
  ApiError,
  type FilingData,
  type FilingHistoryItem,
  type JurisdictionItem,
  type LiabilityStateRow,
  type LiabilitySummary,
  type StateNexusData,
  filingApi,
  liabilityApi,
  nexusStatusApi,
} from '../../services/api';
import { fmtUSD as fmt, fmtDate, fmtPct } from '../../utils/fmt';
import FilingHistory from './FilingHistory';

type TaxTab = 'upcoming' | 'history';
type StatusFilter = 'all' | 'ready' | 'approved' | 'pending_registration';
type ViewMode = 'list' | 'grid';
type JurisdictionData = JurisdictionItem[] | 'loading' | 'error' | 'forbidden';
type FilingDetail = FilingData | 'loading' | 'error' | 'forbidden';

function statusOf(e: unknown): 'forbidden' | 'error' {
  return e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error';
}

// Cards can show the same state for several periods once history is merged in,
// so per-row state is keyed by state+period rather than state alone.
function rowKey(r: { state_code: string; period: string }): string {
  return `${r.state_code}|${r.period}`;
}

function historyToRow(item: FilingHistoryItem): LiabilityStateRow {
  return {
    state_code: item.state_code,
    state_name: item.state_name,
    period: item.period,
    total_liability: item.tax_due ?? 0,
    status: item.status === 'pending' || item.status === 'ready' ? 'due' : 'filed',
    due_date: null,
    days_until_due: null,
    filing_frequency: item.frequency,
    filing_generated_at: item.generated_at,
    qbo_synced_at: null,
  };
}

async function fetchAllHistoryRows(organizationId: string): Promise<LiabilityStateRow[]> {
  const first = await filingApi.getHistory(organizationId, 1);
  const rest = await Promise.all(
    Array.from({ length: first.pages - 1 }, (_, i) => filingApi.getHistory(organizationId, i + 2)),
  );
  return [first, ...rest].flatMap((p) => p.items.map(historyToRow));
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function RefreshIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function DownloadIcon({ size = 13 }: { size?: number }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function EyeIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function FilePdfIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M2 7L5.5 10.5 11 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M2.5 6.5h8M7 3l3.5 3.5L7 10"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AlertLineIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      {open ? (
        <path
          d="M3 9l4-4 4 4"
          stroke="#525866"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M3 5l4 4 4-4"
          stroke="#525866"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function ListViewIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function GridViewIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

// ── Deadline alert helpers (unchanged from prior implementation) ──────────────

interface UrgencyStyle {
  text: string;
  bg: string;
  border: string;
}

function urgencyStyle(row: LiabilityStateRow): UrgencyStyle {
  const d = row.days_until_due ?? 999;
  if (row.status === 'overdue' || d <= 3)
    return { text: '#BE123C', bg: '#FFF1F2', border: '#FECDD3' };
  if (d <= 7) return { text: '#DC2626', bg: '#FFF1F2', border: '#FECDD3' };
  if (d <= 14) return { text: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' };
  return { text: '#B45309', bg: '#FFFBEB', border: '#FDE68A' };
}

function countdownText(row: LiabilityStateRow): string {
  if (row.status === 'overdue') return 'OVERDUE';
  const d = row.days_until_due ?? 0;
  if (d === 0) return 'Due today';
  if (d === 1) return 'Due tomorrow';
  return `Due in ${d} days`;
}

function UrgentBanner({ row }: { row: LiabilityStateRow }): JSX.Element {
  const s = urgencyStyle(row);
  const liabilityStr = row.total_liability ? ` · ${fmt(row.total_liability)} due` : '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 8,
        padding: '10px 14px',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: s.text,
          flexShrink: 0,
          animation: row.status === 'overdue' ? 'pulse 1.5s infinite' : undefined,
        }}
      />
      <span style={{ fontSize: 13, fontWeight: 700, color: s.text }}>{countdownText(row)}</span>
      <span style={{ fontSize: 13, color: s.text }}>
        — {row.state_name} {row.filing_frequency ?? ''} filing{liabilityStr}
      </span>
    </div>
  );
}

function UpcomingCard({ row }: { row: LiabilityStateRow }): JSX.Element {
  const s = urgencyStyle(row);
  const dueStr = row.due_date
    ? new Date(row.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${s.border}`,
        borderLeft: `3px solid ${s.text}`,
        borderRadius: 10,
        padding: '12px 16px',
        minWidth: 180,
        flex: '0 0 auto',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: s.text, marginBottom: 4 }}>
        {countdownText(row)}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{row.state_name}</div>
      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
        {row.filing_frequency ?? ''}
        {dueStr ? ` · due ${dueStr}` : ''}
      </div>
    </div>
  );
}

interface DeadlineAlertsProps {
  states: LiabilityStateRow[];
  loading: boolean;
}
function DeadlineAlerts({ states, loading }: DeadlineAlertsProps): JSX.Element | null {
  if (loading) return null;

  const overdue = states.filter((s) => s.status === 'overdue');
  const urgent = states.filter(
    (s) =>
      s.status !== 'overdue' &&
      s.status !== 'filed' &&
      s.days_until_due != null &&
      s.days_until_due <= 7,
  );
  const upcoming = states.filter(
    (s) =>
      s.status !== 'overdue' &&
      s.status !== 'filed' &&
      s.days_until_due != null &&
      s.days_until_due > 7 &&
      s.days_until_due <= 30,
  );

  if (overdue.length === 0 && urgent.length === 0 && upcoming.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 10,
        }}
      >
        Filing Deadlines
      </div>
      {[...overdue, ...urgent].map((row) => (
        <UrgentBanner key={row.state_code} row={row} />
      ))}
      {upcoming.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginTop: overdue.length + urgent.length > 0 ? 10 : 0,
          }}
        >
          {upcoming.map((row) => (
            <UpcomingCard key={row.state_code} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Filter tabs + view toggle ──────────────────────────────────────────────────

const FILTER_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Alerts & Patterns' },
  { id: 'ready', label: 'Ready for Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'pending_registration', label: 'Pending Registration' },
];

interface FilterTabsRowProps {
  active: StatusFilter;
  onChange: (f: StatusFilter) => void;
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  mobile: boolean;
}
function FilterTabsRow({
  active,
  onChange,
  view,
  onViewChange,
  mobile,
}: FilterTabsRowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 16,
        flexWrap: mobile ? 'wrap' : 'nowrap',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 4,
          background: '#F2F5F8',
          border: '1px solid #E1E4EA',
          borderRadius: 10,
          padding: 4,
          flexWrap: 'wrap',
        }}
      >
        {FILTER_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              padding: '6px 14px',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              background: active === t.id ? '#fff' : 'transparent',
              color: active === t.id ? '#2563EB' : '#525866',
              boxShadow: active === t.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => onViewChange('list')}
          aria-label="List view"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '7px 10px',
            border: '1px solid #E1E4EA',
            borderRadius: '8px 0 0 8px',
            borderRight: 'none',
            background: view === 'list' ? '#fff' : '#F2F5F8',
            color: view === 'list' ? '#0E121B' : '#94A3B8',
            cursor: 'pointer',
          }}
        >
          <ListViewIcon />
        </button>
        <button
          type="button"
          onClick={() => onViewChange('grid')}
          aria-label="Grid view"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '7px 10px',
            border: '1px solid #E1E4EA',
            borderRadius: '0 8px 8px 0',
            background: view === 'grid' ? '#fff' : '#F2F5F8',
            color: view === 'grid' ? '#0E121B' : '#94A3B8',
            cursor: 'pointer',
          }}
        >
          <GridViewIcon />
        </button>
      </div>
    </div>
  );
}

// ── Jurisdiction breakdown (blue variant) ──────────────────────────────────────

function JurisdictionBreakdown({ data }: { data: JurisdictionData | undefined }): JSX.Element {
  if (!data || data === 'loading')
    return <div className="skeleton-box" style={{ width: '100%', height: 90, borderRadius: 8 }} />;
  if (data === 'forbidden')
    return (
      <div style={{ fontSize: 12, color: '#94A3B8' }}>You don't have permission to view this.</div>
    );
  if (data === 'error')
    return (
      <div style={{ fontSize: 12, color: '#BE123C' }}>Failed to load jurisdiction breakdown.</div>
    );
  if (data.length === 0)
    return <div style={{ fontSize: 12, color: '#94A3B8' }}>No jurisdictions for this period.</div>;

  const total = data.reduce((s, j) => s + (j.tax_amount ?? 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {data.map((j) => (
        <div
          key={`${j.jurisdiction_level}-${j.jurisdiction_name}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#0E121B' }}>
              {j.jurisdiction_name} ({fmtPct(j.rate, 1)})
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0E121B' }}>
              {fmt(j.tax_amount)}
            </span>
          </div>
          <ProgressBar
            pct={total > 0 ? (j.tax_amount / total) * 100 : 0}
            color="#2563EB"
            trackColor="#E8EAF2"
            height={7}
            margin="0"
          />
        </div>
      ))}
    </div>
  );
}

// ── Metrics strip ───────────────────────────────────────────────────────────────

function MetricsStrip({
  data,
  mobile,
}: { data: FilingDetail | undefined; mobile: boolean }): JSX.Element {
  if (!data || data === 'loading') {
    return (
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {['gross', 'exempt', 'taxable', 'collected', 'due'].map((key) => (
          <div
            key={key}
            className="skeleton-box"
            style={{ flex: mobile ? '1 1 45%' : 1, height: 52, borderRadius: 8 }}
          />
        ))}
      </div>
    );
  }
  if (data === 'forbidden')
    return (
      <div style={{ fontSize: 12, color: '#94A3B8' }}>You don't have permission to view this.</div>
    );
  if (data === 'error')
    return <div style={{ fontSize: 12, color: '#BE123C' }}>Failed to load filing details.</div>;

  const cells: { label: string; value: string }[] = [
    { label: 'Gross Sales', value: fmt(data.gross_sales) },
    { label: 'Exempt Sales', value: fmt(data.exempt_sales) },
    { label: 'Taxable Sales', value: fmt(data.taxable_sales) },
    { label: 'Tax Collected', value: fmt(data.tax_collected) },
    { label: 'Tax Due', value: fmt(data.tax_due) },
  ];
  return (
    <div style={{ display: 'flex', gap: 24, rowGap: 16, flexWrap: 'wrap' }}>
      {cells.map((c) => (
        <div key={c.label} style={{ flex: mobile ? '1 1 45%' : 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#525866', marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0E121B', letterSpacing: '-0.01em' }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Meta panel ────────────────────────────────────────────────────────────────
// NOTE: Figma also calls for "Generated by" (engine name) and audit-trail notes
// (e.g. "Sharan: SER XML generated…") — neither LiabilityStateRow nor FilingData
// carries those fields, so they're omitted here rather than faked. Last Updated
// uses the real filing_generated_at field.

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

function MetaPanel({ row }: { row: LiabilityStateRow }): JSX.Element {
  return (
    <div style={{ background: '#F4F5FE', borderRadius: 10, padding: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#717784',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 4,
        }}
      >
        Last Updated
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0E121B' }}>
        {fmtDateTime(row.filing_generated_at)}
      </div>
    </div>
  );
}

// ── Status badge + state chip ─────────────────────────────────────────────────

interface BadgeCfg {
  label: string;
  bg: string;
  text: string;
}
function statusBadgeCfg(row: LiabilityStateRow): BadgeCfg {
  return row.status === 'filed'
    ? { label: 'Approved', bg: '#E0FAEC', text: '#1FC16B' }
    : { label: 'Ready for Review', bg: '#FFF1EB', text: '#E97135' };
}

function StatusBadge({ cfg }: { cfg: BadgeCfg }): JSX.Element {
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.text,
        borderRadius: 6,
        padding: '4px 12px',
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

function StateChip({ code, pending }: { code: string; pending: boolean }): JSX.Element {
  const bg = pending ? '#FFF1EB' : '#E0FAEC';
  const color = pending ? '#D05E25' : '#178C4E';
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: bg,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 15,
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {code}
    </div>
  );
}

function dueDateText(row: LiabilityStateRow): string {
  if (row.status === 'overdue') return `Overdue · ${fmtDate(row.due_date)}`;
  if (!row.due_date) return '—';
  return `Due ${fmtDate(row.due_date)}${row.days_until_due != null ? ` (${row.days_until_due}d)` : ''}`;
}

// ── Footer action row ───────────────────────────────────────────────────────────

const FOOTER_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  border: '1px solid #E1E4EA',
  background: '#fff',
  color: '#0E121B',
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

interface CardFooterProps {
  row: LiabilityStateRow;
  organizationId: string;
  filingId: string | undefined;
  onViewTransactions: () => void;
  onApproved: () => void;
  mobile: boolean;
}
function CardFooter({
  row,
  organizationId,
  filingId,
  onViewTransactions,
  onApproved,
  mobile,
}: CardFooterProps): JSX.Element {
  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const approved = row.status === 'filed';

  function handleApprove(): void {
    setApproving(true);
    setActionError(null);
    filingApi
      .approve(organizationId, row.state_code, row.period)
      .then(onApproved)
      .catch((e: Error) => setActionError(e.message))
      .finally(() => setApproving(false));
  }

  function handleDownload(format: string): void {
    if (!filingId) return;
    setDownloading(true);
    setActionError(null);
    filingApi
      .downloadFiling(organizationId, filingId, format)
      .catch((e: Error) => setActionError(e.message))
      .finally(() => setDownloading(false));
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: mobile ? 'wrap' : 'nowrap' }}>
        <button type="button" onClick={onViewTransactions} style={FOOTER_BTN}>
          <EyeIcon /> View transactions
        </button>
        {filingId && (
          <button
            type="button"
            onClick={() => handleDownload('xml')}
            disabled={downloading}
            style={{ ...FOOTER_BTN, color: downloading ? '#94A3B8' : '#0E121B' }}
          >
            <DownloadIcon /> {downloading ? 'Downloading…' : 'Download XML'}
          </button>
        )}
        {filingId && (
          <button
            type="button"
            onClick={() => handleDownload('csv')}
            disabled={downloading}
            style={{ ...FOOTER_BTN, color: downloading ? '#94A3B8' : '#0E121B' }}
          >
            <FilePdfIcon /> Download CSV
          </button>
        )}
        {approved ? (
          <span
            style={{
              ...FOOTER_BTN,
              background: '#1DAF61',
              color: '#fff',
              border: 'none',
              cursor: 'default',
            }}
          >
            <CheckIcon /> Approved
          </span>
        ) : (
          <button
            type="button"
            onClick={handleApprove}
            disabled={approving}
            style={{
              ...FOOTER_BTN,
              background: approving ? '#94A3B8' : '#1DAF61',
              color: '#fff',
              border: 'none',
              cursor: approving ? 'default' : 'pointer',
            }}
          >
            <CheckIcon /> {approving ? 'Approving…' : 'Approve & Download'}
          </button>
        )}
      </div>
      {actionError && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#BE123C' }}>{actionError}</div>
      )}
    </div>
  );
}

// ── Filing card ───────────────────────────────────────────────────────────────

interface FilingCardProps {
  row: LiabilityStateRow;
  organizationId: string;
  expanded: boolean;
  onToggle: () => void;
  detail: FilingDetail | undefined;
  mobile: boolean;
  onApproved: () => void;
  onViewTransactions: () => void;
}
function FilingCard({
  row,
  organizationId,
  expanded,
  onToggle,
  detail,
  mobile,
  onApproved,
  onViewTransactions,
}: FilingCardProps): JSX.Element {
  const badge = statusBadgeCfg(row);
  const loaded =
    detail && detail !== 'loading' && detail !== 'error' && detail !== 'forbidden'
      ? detail
      : undefined;
  const filingId = loaded?.filing_id;
  const jData: JurisdictionData | undefined =
    loaded ? loaded.jurisdictions
    : detail === 'loading' || detail === 'error' || detail === 'forbidden' ? detail
    : undefined;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E1E4EA',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          flexWrap: mobile ? 'wrap' : 'nowrap',
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: '14px 20px',
          font: 'inherit',
        }}
      >
        <StateChip code={row.state_code} pending={false} />
        <div style={{ flex: mobile ? '1 1 100%' : 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0E121B' }}>
            {row.state_name} {row.period}
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#717784',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              marginTop: 2,
            }}
          >
            {row.filing_frequency ?? ''} Filing
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}
          >
            <span style={{ fontSize: 13, color: '#0E121B', whiteSpace: 'nowrap' }}>
              {dueDateText(row)}
            </span>
            <StatusBadge cfg={badge} />
          </div>
          <ChevronIcon open={expanded} />
        </div>
      </button>

      {expanded && (
        <>
          <div style={{ borderTop: '1px solid #EDEFF3', padding: '16px 20px' }}>
            <MetricsStrip data={detail} mobile={mobile} />
          </div>
          <div
            style={{
              borderTop: '1px solid #EDEFF3',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: mobile ? 'column' : 'row',
              gap: 24,
            }}
          >
            <div style={{ flex: 2, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#0E121B',
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  marginBottom: 12,
                }}
              >
                Jurisdiction Breakdown
              </div>
              <JurisdictionBreakdown data={jData} />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: mobile ? 0 : 240,
                maxWidth: mobile ? undefined : 300,
              }}
            >
              <MetaPanel row={row} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid #EDEFF3', padding: '14px 20px' }}>
            <CardFooter
              row={row}
              organizationId={organizationId}
              filingId={filingId}
              onViewTransactions={onViewTransactions}
              onApproved={onApproved}
              mobile={mobile}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ── Pending-registration card ──────────────────────────────────────────────────
// NOTE: no filing period/due date exists yet for a state that hasn't registered,
// so the "Deadline: …" line from Figma is omitted rather than fabricated — real
// data used here is nexus %, revenue, and threshold from nexusStatusApi.

function PendingRegistrationCard({
  state,
  mobile,
}: { state: StateNexusData; mobile: boolean }): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const badge: BadgeCfg = { label: 'Pending Registration', bg: '#F5F7FA', text: '#717784' };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E1E4EA',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          flexWrap: mobile ? 'wrap' : 'nowrap',
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: '14px 20px',
          font: 'inherit',
        }}
      >
        <StateChip code={state.stateCode} pending />
        <div style={{ flex: mobile ? '1 1 100%' : 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0E121B' }}>{state.stateName}</div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#717784',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              marginTop: 2,
            }}
          >
            Registration required
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <StatusBadge cfg={badge} />
          <ChevronIcon open={expanded} />
        </div>
      </button>

      {expanded && (
        <div
          style={{
            background: '#FFF1EB',
            border: '1px solid #FFBA97',
            borderRadius: 8,
            padding: 16,
            margin: '0 20px 16px',
            display: 'flex',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: '#E97135',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertLineIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0E121B', marginBottom: 6 }}>
              Registration required before this return can be generated.
            </div>
            <div style={{ fontSize: 12, color: '#9F1239', lineHeight: 1.55, marginBottom: 10 }}>
              Direct sales reached {(state.revenueThresholdPct ?? 0).toFixed(0)}% of the{' '}
              {state.stateCode} economic nexus threshold ({fmt(state.revenue)} of{' '}
              {fmt(state.revenueThreshold)}). Register for a sales tax permit before a return can be
              filed for this state.
            </div>
            <a
              href="https://www.streamlinedsalestax.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#E97135',
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              Register Now <ArrowRightIcon />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab navigation (Upcoming / History) ────────────────────────────────────────

function TabNav({
  active,
  onChange,
}: { active: TaxTab; onChange: (t: TaxTab) => void }): JSX.Element {
  const tabs: { id: TaxTab; label: string }[] = [
    { id: 'upcoming', label: 'Upcoming Filings' },
    { id: 'history', label: 'Filing History' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        background: '#F1F5F9',
        borderRadius: 10,
        padding: 3,
        marginBottom: 20,
        alignSelf: 'flex-start',
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          style={{
            padding: '6px 16px',
            border: 'none',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.12s',
            background: active === t.id ? '#fff' : 'transparent',
            color: active === t.id ? '#0F172A' : '#64748B',
            boxShadow: active === t.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Sort + CSV helpers ──────────────────────────────────────────────────────────

function sortStates(states: LiabilityStateRow[]): LiabilityStateRow[] {
  return [...states].sort((a, b) => {
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (b.status === 'overdue' && a.status !== 'overdue') return 1;
    if (a.status === 'filed' && b.status !== 'filed') return 1;
    if (b.status === 'filed' && a.status !== 'filed') return -1;
    const byDue = (a.days_until_due ?? 999) - (b.days_until_due ?? 999);
    return byDue !== 0 ? byDue : b.period.localeCompare(a.period);
  });
}

function downloadFilingsCsv(states: LiabilityStateRow[]): void {
  const header = [
    'State Code',
    'State Name',
    'Period',
    'Frequency',
    'Status',
    'Due Date',
    'Total Liability',
  ];
  const rows = states.map((s) => [
    s.state_code,
    s.state_name,
    s.period,
    s.filing_frequency ?? '',
    s.status,
    s.due_date ?? '',
    String(s.total_liability),
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tax-returns.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Page header ─────────────────────────────────────────────────────────────────

function PageHeader({
  onRefresh,
  onDownload,
  mobile,
}: { onRefresh: () => void; onDownload: () => void; mobile: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 16,
        flexWrap: mobile ? 'wrap' : 'nowrap',
      }}
    >
      <div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#0E121B' }}>Tax Returns</div>
        <div style={{ fontSize: 14, color: '#525866', marginTop: 2 }}>
          Manage and track all your jurisdiction tax filings
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={onRefresh}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '6px 13px',
            background: '#fff',
            border: '1px solid #E1E4EA',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: '#475569',
            cursor: 'pointer',
          }}
        >
          <RefreshIcon /> Refresh
        </button>
        <button
          type="button"
          onClick={onDownload}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 16px',
            background: '#2563EB',
            border: 'none',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <DownloadIcon /> Download
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface TaxReturnsProps {
  onReviewFiling?: (stateCode: string, stateName: string, period: string) => void;
}

export default function TaxReturns({ onReviewFiling }: TaxReturnsProps): JSX.Element {
  const { organizationId } = useAuth();
  const mobile = useWindowWidth() < 768;
  const [tab, setTab] = useState<TaxTab>('upcoming');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [view, setView] = useState<ViewMode>('list');
  const [summary, setSummary] = useState<LiabilitySummary | null>(null);
  const [nexusStates, setNexusStates] = useState<StateNexusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [history, setHistory] = useState<LiabilityStateRow[]>([]);
  const [filings, setFilings] = useState<Record<string, FilingDetail>>({});

  const fetchRowDetails = useCallback(
    (row: LiabilityStateRow): void => {
      const key = rowKey(row);
      setFilings((prev) => {
        if (prev[key] && prev[key] !== 'error') return prev;
        filingApi
          .getReview(organizationId, row.state_code, row.period)
          .then((data) => setFilings((p) => ({ ...p, [key]: data })))
          .catch((e) => setFilings((p) => ({ ...p, [key]: statusOf(e) })));
        return { ...prev, [key]: 'loading' };
      });
    },
    [organizationId],
  );

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    Promise.allSettled([
      liabilityApi.getSummary(organizationId),
      nexusStatusApi.getStatus(organizationId),
      fetchAllHistoryRows(organizationId),
    ])
      .then(([summaryRes, nexusRes, historyRes]) => {
        if (summaryRes.status === 'fulfilled') {
          setSummary(summaryRes.value);
          const current = new Set(summaryRes.value.states.map(rowKey));
          setHistory(
            historyRes.status === 'fulfilled'
              ? historyRes.value.filter((r) => !current.has(rowKey(r)))
              : [],
          );
          setExpanded((prev) => {
            if (prev.size > 0) return prev;
            const firstRow = sortStates(summaryRes.value.states)[0];
            if (!firstRow) return prev;
            fetchRowDetails(firstRow);
            return new Set([rowKey(firstRow)]);
          });
        } else {
          setError((summaryRes.reason as Error)?.message ?? 'Failed to load');
          setErrorStatus(summaryRes.reason instanceof ApiError ? summaryRes.reason.status : null);
        }
        setNexusStates(nexusRes.status === 'fulfilled' ? nexusRes.value : []);
      })
      .finally(() => setLoading(false));
  }, [organizationId, fetchRowDetails]);

  useEffect(load, [load]);

  function toggleRow(row: LiabilityStateRow): void {
    const key = rowKey(row);
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    fetchRowDetails(row);
  }

  function handleApproved(row: LiabilityStateRow): void {
    const key = rowKey(row);
    const markFiled = (s: LiabilityStateRow): LiabilityStateRow =>
      rowKey(s) === key ? { ...s, status: 'filed' as const } : s;
    setSummary((prev) => (prev ? { ...prev, states: prev.states.map(markFiled) } : prev));
    setHistory((prev) => prev.map(markFiled));
    setFilings((prev) => {
      const existing = prev[key];
      if (existing && existing !== 'loading' && existing !== 'error' && existing !== 'forbidden') {
        return { ...prev, [key]: { ...existing, status: 'filed' as const } };
      }
      return prev;
    });
  }

  if (error && errorStatus === 403)
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
          }}
        >
          <span style={{ fontSize: 13, color: '#BE123C' }}>
            You don't have permission to view this — ask your organization admin for access.
          </span>
        </div>
      </div>
    );

  if (error)
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, color: '#BE123C', flex: 1 }}>{error}</span>
          <button
            type="button"
            onClick={load}
            style={{
              padding: '6px 14px',
              background: '#0F172A',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );

  const sorted = sortStates([...(summary?.states ?? []), ...history]);
  const pendingRegistrationStates = nexusStates.filter(
    (s) => s.nexusTriggered && s.registrationStatus !== 'registered',
  );

  const showFiling = (r: LiabilityStateRow): boolean =>
    statusFilter === 'all' ||
    (statusFilter === 'ready' && r.status !== 'filed') ||
    (statusFilter === 'approved' && r.status === 'filed');
  const filingCards = statusFilter === 'pending_registration' ? [] : sorted.filter(showFiling);
  const showPending = statusFilter === 'all' || statusFilter === 'pending_registration';

  return (
    <div
      style={{
        padding: '18px 22px',
        background: '#F8FAFC',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <PageHeader onRefresh={load} onDownload={() => downloadFilingsCsv(sorted)} mobile={mobile} />
      <TabNav active={tab} onChange={setTab} />

      {tab === 'upcoming' && (
        <>
          <DeadlineAlerts states={summary?.states ?? []} loading={loading} />
          <FilterTabsRow
            active={statusFilter}
            onChange={setStatusFilter}
            view={view}
            onViewChange={setView}
            mobile={mobile}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {loading ? (
              ['sk-1', 'sk-2', 'sk-3'].map((key) => (
                <div
                  key={key}
                  className="skeleton-box"
                  style={{ width: '100%', height: 90, borderRadius: 6 }}
                />
              ))
            ) : filingCards.length === 0 &&
              (!showPending || pendingRegistrationStates.length === 0) ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#94A3B8',
                  fontSize: 13,
                  background: '#fff',
                  border: '1px solid #E1E4EA',
                  borderRadius: 6,
                }}
              >
                No filings match this filter.
              </div>
            ) : (
              <>
                {filingCards.map((row) => (
                  <FilingCard
                    key={rowKey(row)}
                    row={row}
                    organizationId={organizationId}
                    expanded={expanded.has(rowKey(row))}
                    onToggle={() => toggleRow(row)}
                    detail={filings[rowKey(row)]}
                    mobile={mobile}
                    onApproved={() => handleApproved(row)}
                    onViewTransactions={() =>
                      onReviewFiling?.(row.state_code, row.state_name, row.period)
                    }
                  />
                ))}
                {showPending &&
                  pendingRegistrationStates.map((state) => (
                    <PendingRegistrationCard key={state.stateCode} state={state} mobile={mobile} />
                  ))}
              </>
            )}
          </div>
        </>
      )}

      {tab === 'history' && (
        <FilingHistory
          onReviewFiling={(stateCode, stateName, period) =>
            onReviewFiling?.(stateCode, stateName, period)
          }
        />
      )}
    </div>
  );
}
